import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CONTAINER,
  EYEBROW,
  FONT_DISPLAY,
  SECTION_H2,
  SECTION_PAD,
} from './marketingStyles';

// "A week with DodoSkills" (owner-approved mockup, 2026-09-27): five steps
// per audience, each saying where in the app it happens. Only what the
// product does today -- the schools' school-wide view is marked "Coming
// next" until it exists.

type Audience = 'teachers' | 'parents' | 'schools';

type Step = { title: string; text: string; where: string; soon?: boolean };

const content: Record<
  Audience,
  { label: string; cta: string; to: string; footnote: string; steps: Step[] }
> = {
  teachers: {
    label: 'For teachers',
    cta: 'Explore the teacher workspace',
    to: '/auth/sign-up?role=teacher',
    footnote:
      'Students never need an account, an email or a password — just their code. You can add a nickname to tell them apart — only you can see it. Don’t use a full name.',
    steps: [
      {
        title: 'Add your students',
        text: 'Add them by year level. Each gets an anonymous reference like “Student 1” — no names needed.',
        where: 'Your students',
      },
      {
        title: 'Assign work',
        text: 'Pick a lesson from the Australian Curriculum for a year level or a group — for now or a later day.',
        where: 'Assign work',
      },
      {
        title: 'Give students their codes',
        text: 'Each student has their own login code, printed once. They type it, or scan its QR code, at dodoskills.com.au/learn.',
        where: 'Login codes',
      },
      {
        title: 'Students practise',
        text: 'Focused questions, no games or rewards. Every answer is recorded for you.',
        where: 'On the student’s device',
      },
      {
        title: 'See what to teach next',
        text: 'See which skills are secure, where errors cluster, and who needs a follow-up.',
        where: 'Analytics · Evidence review',
      },
    ],
  },
  parents: {
    label: 'For parents',
    cta: 'Start as a parent',
    to: '/auth/sign-up?role=parent_carer',
    footnote:
      'Your child is known by the nickname you choose. It is only ever shown to you, never to other learners or publicly.',
    steps: [
      {
        title: 'Add your child',
        text: 'A nickname and their year level — nothing else about them.',
        where: 'Parent home',
      },
      {
        title: 'Connect to their class',
        text: 'Optional: enter the code from their teacher to see their class work too.',
        where: 'Connect to school',
      },
      {
        title: 'Set some practice',
        text: 'Pick a lesson for their year level, today or on a day you choose.',
        where: 'Assign work',
      },
      {
        title: 'Give them their code',
        text: 'Your child has their own login code, under “Your child’s login code”. They type it, or scan its QR code, at dodoskills.com.au/learn and practise.',
        where: 'Your child’s code',
      },
      {
        title: 'See how they’re going',
        text: 'How many they got right, each answer, and their progress by skill — in plain words.',
        where: 'Results',
      },
    ],
  },
  schools: {
    label: 'For schools',
    cta: 'Register your school',
    to: '/auth/sign-up?role=school',
    footnote:
      'Linking is each teacher’s choice. Nothing about a teacher’s classes is shared with the school until the teacher agrees.',
    steps: [
      {
        title: 'Register your school',
        text: 'A school admin signs up and sends the school’s details for checking.',
        where: 'Register your school',
      },
      {
        title: 'We check it by hand',
        text: 'DodoSkills staff confirm the school before it is verified.',
        where: 'We email you when it’s done',
      },
      {
        title: 'Invite your teachers',
        text: 'By email, or approve teachers who might be at your school.',
        where: 'Teachers',
      },
      {
        title: 'Teachers choose to link',
        text: 'Each teacher sees exactly what the school will see before agreeing.',
        where: 'Your school',
      },
      {
        title: 'Follow progress across classes',
        text: 'See results by year level across your linked teachers’ classes.',
        where: 'Reports by year',
      },
    ],
  },
};

const audiences: Audience[] = ['teachers', 'parents', 'schools'];

export function HowItWorks() {
  const [audience, setAudience] = useState<Audience>('teachers');
  const current = content[audience];

  return (
    <div id="how-it-works" className={`${SECTION_PAD} scroll-mt-24`}>
      <div className={`${CONTAINER} flex flex-col gap-8`}>
        <div className="flex w-full flex-col gap-6 xl:flex-row xl:items-end xl:justify-between">
          <div className="flex max-w-[640px] flex-col gap-3">
            <p className={EYEBROW}>How it works</p>
            <h2 className={SECTION_H2}>A week with DodoSkills</h2>
            <p className="text-[16px] leading-[1.6] text-ink-soft lg:text-[17px]">
              Five steps from signing up to knowing what to teach next. Pick who
              you are.
            </p>
          </div>
          <div
            role="tablist"
            aria-label="Who are you?"
            className="flex w-full gap-1 overflow-x-auto rounded-full border border-line bg-paper-2 p-1 sm:w-fit"
          >
            {audiences.map((key) => {
              const selected = key === audience;
              return (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  id={`how-tab-${key}`}
                  aria-selected={selected}
                  aria-controls="how-it-works-steps"
                  onClick={() => {
                    setAudience(key);
                  }}
                  className={`min-h-11 flex-1 rounded-full px-4 text-[14px] font-semibold whitespace-nowrap sm:flex-none sm:px-5 ${
                    selected
                      ? 'bg-forest-950 text-white'
                      : 'text-ink-soft hover:bg-white'
                  }`}
                >
                  {content[key].label}
                </button>
              );
            })}
          </div>
        </div>

        <div
          id="how-it-works-steps"
          role="tabpanel"
          aria-labelledby={`how-tab-${audience}`}
          className="w-full"
        >
          <ol className="grid w-full gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
            {current.steps.map((step, index) => (
              <li
                key={step.title}
                className="flex gap-3 sm:flex-col sm:gap-3.5"
              >
                <div className="flex items-center gap-2.5">
                  <span
                    className={`flex size-10 shrink-0 items-center justify-center rounded-full text-[14px] font-bold ${
                      step.soon
                        ? 'border-[1.5px] border-dashed border-accent bg-accent-soft text-accent'
                        : 'bg-forest-950 text-mint'
                    }`}
                  >
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  {index < current.steps.length - 1 && (
                    <span
                      aria-hidden="true"
                      className="hidden h-0.5 flex-1 bg-line-strong xl:block"
                    />
                  )}
                </div>
                <div
                  className={`flex flex-1 flex-col gap-1.5 rounded-card p-4 sm:gap-2.5 sm:p-5 ${
                    step.soon
                      ? 'border-[1.5px] border-dashed border-line-strong bg-paper'
                      : 'border border-line bg-white'
                  }`}
                >
                  <p
                    className={`${FONT_DISPLAY} text-[19px] leading-tight font-bold text-ink sm:text-[20px] lg:text-[22px]`}
                  >
                    {step.title}
                  </p>
                  <p className="flex-1 text-[14px] leading-[1.55] [overflow-wrap:anywhere] text-ink-soft">
                    {step.text}
                  </p>
                  <p
                    className={`text-[12px] font-semibold ${step.soon ? 'text-accent' : 'text-forest-600'}`}
                  >
                    {step.soon ? 'Coming next' : `Where: ${step.where}`}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </div>

        <div className="flex w-full flex-col gap-4 rounded-card bg-sage px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="grid max-w-[820px] gap-2">
            <p className="text-[15px] text-forest-800">{current.footnote}</p>
            <Link
              to="/guide"
              className="w-fit text-[14px] font-semibold text-forest-800 underline underline-offset-4"
            >
              Read the step-by-step guides
            </Link>
          </div>
          <Link
            to={current.to}
            className="inline-flex min-h-12 shrink-0 items-center justify-center rounded-lg bg-forest-800 px-5 text-[15px] font-semibold whitespace-nowrap text-white hover:bg-forest-950"
          >
            {current.cta}
          </Link>
        </div>
      </div>
    </div>
  );
}
